import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { setChoicesPoints } from "../slices/dataSlice";
import { useDispatch } from "react-redux";

const HEADER_OFFSET = 180; 
const GAP = 12;
const VIEWPORT_PADDING = 8;

const PopupHover = ({ career, parentRef, active, tooltipId }) => {
  const popupRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [placement, setPlacement] = useState("top"); // "top" | "bottom"

  const dispatch = useDispatch();

  const computeAndShow = useCallback(() => {
    if (!parentRef?.current) return;

    const rect = parentRef.current.getBoundingClientRect();
    dispatch(setChoicesPoints(career.choice_source));

    const estimatedHeight = popupRef.current?.offsetHeight ?? 160;

    const spaceAbove = rect.top - HEADER_OFFSET;
    const spaceBelow = window.innerHeight - rect.bottom;

    const shouldFlipToBottom =
      spaceAbove < (estimatedHeight + GAP) && spaceBelow > spaceAbove;

    const nextPlacement = shouldFlipToBottom ? "bottom" : "top";
    setPlacement(nextPlacement);

    const anchorTop =
      nextPlacement === "top"
        ? rect.top + window.scrollY - GAP
        : rect.bottom + window.scrollY + GAP;

    setPosition({
      top: anchorTop,
      left: rect.left + rect.width / 2,
    });

    setVisible(true);
  }, [career, dispatch, parentRef]);

  const hide = useCallback(() => {
    dispatch(setChoicesPoints([]));
    setVisible(false);
  }, [dispatch]);

  useEffect(() => {
    if (!parentRef?.current) return;

    if (active !== undefined) {
      if (active) computeAndShow();
      else hide();
      return;
    }

    const el = parentRef.current;
    el.addEventListener("mouseenter", computeAndShow);
    el.addEventListener("mouseleave", hide);
    el.addEventListener("focusin", computeAndShow);
    el.addEventListener("focusout", hide);

    return () => {
      el.removeEventListener("mouseenter", computeAndShow);
      el.removeEventListener("mouseleave", hide);
      el.removeEventListener("focusin", computeAndShow);
      el.removeEventListener("focusout", hide);
    };
    
  }, [active, computeAndShow, hide, parentRef]);

  
  useEffect(() => {
    if (!visible || !popupRef.current || !parentRef?.current) return;

    const popup = popupRef.current;

  
    const rect = parentRef.current.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();

    const spaceAbove = rect.top - HEADER_OFFSET;
    const spaceBelow = window.innerHeight - rect.bottom;

    const shouldFlipToBottom =
      spaceAbove < (popupRect.height + GAP) && spaceBelow > spaceAbove;

    const nextPlacement = shouldFlipToBottom ? "bottom" : "top";

    if (nextPlacement !== placement) {
      setPlacement(nextPlacement);
      const anchorTop =
        nextPlacement === "top"
          ? rect.top + window.scrollY - GAP
          : rect.bottom + window.scrollY + GAP;

      setPosition((prev) => ({
        ...prev,
        top: anchorTop,
        left: rect.left + rect.width / 2,
      }));

      return;
    }

    const padding = VIEWPORT_PADDING;
    let left = position.left;

    const currentLeft = left - popupRect.width / 2;
    const currentRight = left + popupRect.width / 2;

    if (currentLeft < padding) {
      left += padding - currentLeft;
    }
    if (currentRight > window.innerWidth - padding) {
      left -= currentRight - (window.innerWidth - padding);
    }

    if (left !== position.left) {
      setPosition((prev) => ({ ...prev, left }));
    }
  }, [visible, position.left, placement, parentRef]);

  useEffect(() => {
    if (!visible) return;

    const onMove = () => computeAndShow();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);

    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [computeAndShow, visible]);

  if (!visible) return null;

  const transform =
    placement === "top"
      ? "translate(-50%, -100%)" // above anchor
      : "translate(-50%, 0%)";   // below anchor

  return createPortal(
    <div
      ref={popupRef}
      id={tooltipId}
      role="tooltip"
      style={{
        position: "absolute",
        top: position.top,
        left: position.left,
        transform,
        zIndex: 9999,
      }}
      className={`relative w-80 bg-white border border-border rounded-lg p-4 text-sm text-left
  ${placement === "top"
    ? "shadow-lg"
    : "shadow-[0_-10px_20px_rgba(0,0,0,0.15)]"}
`}
    >
      {placement === "top" ? (
        <div
          aria-hidden="true"
          className="absolute left-1/2 -translate-x-1/2 bottom-[-8px]
                     w-0 h-0
                     border-l-8 border-r-8 border-t-8
                     border-l-transparent border-r-transparent border-t-white"
        />
      ) : (
        <div
          aria-hidden="true"
          className="absolute left-1/2 -translate-x-1/2 top-[-8px]
                     w-0 h-0
                     border-l-8 border-r-8 border-b-8
                     border-l-transparent border-r-transparent border-b-white"
        />
      )}

      <p className="font-semibold">{career.profession_name}</p>
      <p className="text-gray-600 mt-1">{career.rationale}</p>

      <p className="my-3">
        <span className="font-medium">Job demand:</span>{" "}
        <span
          className={`font-semibold text-xs rounded-full px-2 py-1 ${
            {
              high: "bg-success text-white",
              medium: "bg-warning text-white",
              low: "bg-category-highlight text-white",
            }[career?.job_demand?.toLowerCase()] || "bg-border text-text-primary"
          }`}
        >
          {career?.job_demand}
        </span>
      </p>

      <div className="flex justify-between">
        <span className="font-medium">Salary:</span>
        <span>
          ${career.min_salary} – ${career.max_salary}
        </span>
      </div>

      <div className="flex justify-between">
        <span className="font-medium">Years of study:</span>
        <strong>{career.years_of_study}</strong>
      </div>
    </div>,
    document.body
  );
};

export default PopupHover;
